<!--
  @component DeploymentsSection — the /deployments[/:section] dispatcher.
  tier:   deployments router target

  Renders the right thing for the current (section × scope):
    • scope = one  → the per-project screen for that section (currentProject
      is kept in sync by ConsumerRoot's scope→selectProject bridge)
    • section = overview, scope ≠ one → the fleet command center (DeploymentsLanding)
    • any other section, scope ≠ one → a project-picker feed (pick → scope=one)

  Phase 1 will replace the "scope ≠ one" non-overview branch with real
  aggregated views.
-->
<script lang="ts">
  import type { Component } from "svelte";
  import { useScopeIO } from "./ScopeIO.svelte";

  import OverviewScreen from "./OverviewScreen.svelte";
  import ReleasesScreen from "./ReleasesScreen.svelte";
  import CodeScreen from "./CodeScreen.svelte";
  import DomainScreen from "./DomainScreen.svelte";
  import StorageScreen from "./StorageScreen.svelte";
  import DatabaseScreen from "./DatabaseScreen.svelte";
  import PerformanceScreen from "./PerformanceScreen.svelte";
  import LogsScreen from "./LogsScreen.svelte";
  import DeploymentsLanding from "./DeploymentsLanding.svelte";
  import ReleasesAggregate from "./ReleasesAggregate.svelte";
  import LogsAggregate from "./LogsAggregate.svelte";
  import DatabaseAggregate from "./DatabaseAggregate.svelte";
  import StorageAggregate from "./StorageAggregate.svelte";
  import DomainAggregate from "./DomainAggregate.svelte";
  import ComposeCanvas from "./ComposeCanvas.svelte";
  import ActivityFeed from "./ActivityFeed.svelte";
  import CostPanel from "./CostPanel.svelte";
  import SecurityPanel from "./SecurityPanel.svelte";
  import ScopePickerFeed from "./ScopePickerFeed.svelte";

  // svelte-spa-router passes matched params (e.g. { section }).
  let { params }: { params?: { section?: string } } = $props();

  const scope = useScopeIO();
  const section = $derived(params?.section ?? "overview");

  const SINGLE: Record<string, Component> = {
    overview: OverviewScreen,
    releases: ReleasesScreen,
    code: CodeScreen,
    domain: DomainScreen,
    storage: StorageScreen,
    database: DatabaseScreen,
    performance: PerformanceScreen,
    logs: LogsScreen,
  };
  const SingleScreen = $derived(SINGLE[section] ?? OverviewScreen);
</script>

{#if section === "compose"}
  <ComposeCanvas />
{:else if section === "activity"}
  <ActivityFeed />
{:else if section === "cost"}
  <CostPanel />
{:else if section === "security"}
  <SecurityPanel />
{:else if scope.singleId}
  {@const Screen = SingleScreen}
  <Screen />
{:else if section === "overview"}
  <DeploymentsLanding />
{:else if section === "releases"}
  <ReleasesAggregate />
{:else if section === "logs"}
  <LogsAggregate />
{:else if section === "database"}
  <DatabaseAggregate />
{:else if section === "storage"}
  <StorageAggregate />
{:else if section === "domain"}
  <DomainAggregate />
{:else}
  <ScopePickerFeed {section} />
{/if}
